// ─────────────────────────────────────────────────────────────────────────────
// filterValidation.js
// Shared frontend filter validation utilities for all listing pages.
// Import and use these in CommercialSpaces, Businesses, Adss, Franchises, Stalls.
// ─────────────────────────────────────────────────────────────────────────────

// ── Regex patterns ────────────────────────────────────────────────────────────
const LOCALITY_REGEX = /^(?=.*[A-Za-z])[A-Za-z0-9\s.,#&()-]+$/;
const LETTERS_ONLY_REGEX = /^[A-Za-z\s.-]+$/;
const POSITIVE_INT_REGEX = /^\d+$/;

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Returns true if value is an empty string, null, undefined, or whitespace-only.
 */
export const isEmpty = (v) =>
    v === null || v === undefined || (typeof v === "string" && v.trim() === "");

/**
 * Safely convert a value to a positive finite number.
 * Returns null if conversion fails or result is not a positive finite number.
 */
export const toPositiveNumber = (v) => {
    if (isEmpty(v)) return null;
    const n = Number(v);
    if (!isFinite(n) || n < 0) return null;
    return n;
};

/**
 * Validate a locality string.
 * Returns { valid: true } or { valid: false, message: string }
 */
export const validateLocality = (locality) => {
    if (isEmpty(locality)) return { valid: true }; // optional field
    const trimmed = locality.trim();
    if (trimmed.length < 2) {
        return { valid: false, message: "Locality must be at least 2 characters." };
    }
    if (trimmed.length > 100) {
        return { valid: false, message: "Locality must be under 100 characters." };
    }
    if (!LOCALITY_REGEX.test(trimmed)) {
        return {
            valid: false,
            message:
                "Locality must contain at least one letter. Allowed special characters: . , # & ( ) -",
        };
    }
    return { valid: true };
};

/**
 * Validate a min/max numeric pair.
 * fieldLabel: e.g. "Price", "Area"
 * Returns { valid: true } or { valid: false, message: string }
 */
export const validateNumericRange = (min, max, fieldLabel = "Value") => {
    if (!isEmpty(min)) {
        if (!POSITIVE_INT_REGEX.test(String(min))) {
            return {
                valid: false,
                message: `${fieldLabel} Min must be a positive whole number.`,
            };
        }

        const n = Number(min);

        if (!isFinite(n) || n < 0) {
            return {
                valid: false,
                message: `${fieldLabel} Min must be a valid positive number.`,
            };
        }
    }

    if (!isEmpty(max)) {
        if (!POSITIVE_INT_REGEX.test(String(max))) {
            return {
                valid: false,
                message: `${fieldLabel} Max must be a positive whole number.`,
            };
        }

        const n = Number(max);

        if (!isFinite(n) || n < 0) {
            return {
                valid: false,
                message: `${fieldLabel} Max must be a valid positive number.`,
            };
        }
    }

    if (!isEmpty(min) && !isEmpty(max)) {
        if (Number(min) > Number(max)) {
            return {
                valid: false,
                message: `${fieldLabel} Min cannot be greater than Max.`,
            };
        }
    }

    return { valid: true };
};

/**
 * Validate a single positive integer field (e.g. floor, maxAge).
 */
export const validatePositiveInteger = (value, fieldLabel = "Value") => {
    if (isEmpty(value)) return { valid: true }; // optional
    const n = Number(value);
    if (!isFinite(n)) return { valid: false, message: `${fieldLabel} must be a valid number.` };
    if (n < 0) return { valid: false, message: `${fieldLabel} cannot be negative.` };
    if (!Number.isInteger(n)) return { valid: false, message: `${fieldLabel} must be a whole number.` };
    return { valid: true };
};

/**
 * Validate a year value.
 */
export const validateYear = (value, fieldLabel = "Year") => {
    if (isEmpty(value)) return { valid: true };
    const n = Number(value);
    if (!isFinite(n) || !Number.isInteger(n)) {
        return { valid: false, message: `${fieldLabel} must be a valid year.` };
    }
    if (n < 1800 || n > new Date().getFullYear()) {
        return {
            valid: false,
            message: `${fieldLabel} must be between 1800 and ${new Date().getFullYear()}.`,
        };
    }
    return { valid: true };
};

/**
 * Validate custom text input (for "other" option overrides).
 * Must contain at least one letter, no special injection chars.
 */
export const validateCustomText = (value, fieldLabel = "Value") => {
    if (isEmpty(value)) return { valid: true };
    const trimmed = value.trim();
    if (trimmed.length < 2) {
        return { valid: false, message: `${fieldLabel} must be at least 2 characters.` };
    }
    if (trimmed.length > 100) {
        return { valid: false, message: `${fieldLabel} must be under 100 characters.` };
    }
    if (!/(?=.*[A-Za-z])/.test(trimmed)) {
        return { valid: false, message: `${fieldLabel} must contain at least one letter.` };
    }
    // Disallow script injection patterns
    if (/<|>|script|javascript|eval\s*\(/i.test(trimmed)) {
        return { valid: false, message: `${fieldLabel} contains invalid characters.` };
    }
    return { valid: true };
};

/**
 * Strip HTML / script injection from a string (lightweight sanitize for frontend).
 */
export const sanitizeString = (v) => {
    if (typeof v !== "string") return "";
    return v
        .replace(/<[^>]*>/g, "")
        .replace(/javascript:/gi, "")
        .replace(/on\w+=/gi, "")
        .trim();
};

/**
 * Clamp a numeric string so it doesn't go below 0.
 * Returns "" if the value is invalid.
 */
export const clampNonNegative = (v) => {
    if (isEmpty(v)) return "";
    const n = Number(v);
    if (!isFinite(n)) return "";
    return String(Math.max(0, n));
};

/**
 * Validate all CommercialSpaces filters.
 * Returns { valid: true } or { valid: false, message: string }
 */
export const validateCommercialFilters = (filters, customPropertyType) => {
    const localityCheck = validateLocality(filters.locality);
    if (!localityCheck.valid) return localityCheck;

    const priceCheck = validateNumericRange(filters.minPrice, filters.maxPrice, "Price");
    if (!priceCheck.valid) return priceCheck;

    const areaCheck = validateNumericRange(filters.minArea, filters.maxArea, "Area");
    if (!areaCheck.valid) return areaCheck;

    const ageCheck = validatePositiveInteger(filters.maxAge, "Max Property Age");
    if (!ageCheck.valid) return ageCheck;

    const floorCheck = validatePositiveInteger(filters.floor, "Floor");
    if (!floorCheck.valid) return floorCheck;

    if (filters.propertyType === "other") {
        const customCheck = validateCustomText(customPropertyType, "Custom Property Type");
        if (!customCheck.valid) return customCheck;
    }

    return { valid: true };
};

/**
 * Validate all Businesses filters.
 */
export const validateBusinessFilters = (filters, customIndustry, customBusinessType) => {
    const localityCheck = validateLocality(filters.locality);
    if (!localityCheck.valid) return localityCheck;

    const minYearCheck = validateYear(filters.minYear, "Min Founded Year");
    if (!minYearCheck.valid) return minYearCheck;

    const maxYearCheck = validateYear(filters.maxYear, "Max Founded Year");
    if (!maxYearCheck.valid) return maxYearCheck;

    if (!isEmpty(filters.minYear) && !isEmpty(filters.maxYear)) {
        if (Number(filters.minYear) > Number(filters.maxYear)) {
            return { valid: false, message: "Min Founded Year cannot be greater than Max Founded Year." };
        }
    }

    if (filters.industry?.includes("other")) {
        const customCheck = validateCustomText(customIndustry, "Custom Industry");
        if (!customCheck.valid) return customCheck;
    }

    if (filters.type === "other") {
        const customCheck = validateCustomText(customBusinessType, "Custom Business Type");
        if (!customCheck.valid) return customCheck;
    }

    return { valid: true };
};

/**
 * Validate all Ads filters.
 */
export const validateAdsFilters = (filters) => {
    const localityCheck = validateLocality(filters.locality);
    if (!localityCheck.valid) return localityCheck;

    const priceCheck = validateNumericRange(filters.minPrice, filters.maxPrice, "Price");
    if (!priceCheck.valid) return priceCheck;

    return { valid: true };
};

/**
 * Validate all Franchise filters.
 */
export const validateFranchiseFilters = (filters, customCategory) => {
    const localityCheck = validateLocality(filters.locality);
    if (!localityCheck.valid) return localityCheck;

    const investmentCheck = validateNumericRange(
        filters.minInvestment,
        filters.maxInvestment,
        "Investment"
    );
    if (!investmentCheck.valid) return investmentCheck;

    const areaCheck = validateNumericRange(filters.minArea, filters.maxArea, "Area");
    if (!areaCheck.valid) return areaCheck;

    const outletCheck = validateNumericRange(filters.minOutlet, filters.maxOutlet, "Outlet Count");
    if (!outletCheck.valid) return outletCheck;

    const feeCheck = validateNumericRange(filters.minFee, filters.maxFee, "Franchise Fee");
    if (!feeCheck.valid) return feeCheck;

    const royaltyCheck = validateNumericRange(filters.minRoyalty, filters.maxRoyalty, "Royalty Fee");
    if (!royaltyCheck.valid) return royaltyCheck;

    if (filters.category === "other") {
        const customCheck = validateCustomText(customCategory, "Custom Category");
        if (!customCheck.valid) return customCheck;
    }

    return { valid: true };
};

/**
 * Validate all Stalls filters.
 */
export const validateStallFilters = (filters, customStallType) => {
    const localityCheck = validateLocality(filters.locality);
    if (!localityCheck.valid) return localityCheck;

    const priceCheck = validateNumericRange(filters.minPrice, filters.maxPrice, "Price");
    if (!priceCheck.valid) return priceCheck;

    const areaCheck = validateNumericRange(filters.minArea, filters.maxArea, "Area");
    if (!areaCheck.valid) return areaCheck;

    if (filters.stallType === "other") {
        const customCheck = validateCustomText(customStallType, "Custom Stall Type");
        if (!customCheck.valid) return customCheck;
    }

    return { valid: true };
};